/**
 * Unit detail.
 *
 * One unit, in enough depth to decide whether to go and look at it. Four things
 * on this page are here because of how these units actually behave rather than
 * because a dashboard usually has them:
 *
 *  - The chart header states the RESOLUTION the server actually used and how
 *    many samples in range came off a clock we do not trust. A chart of hourly
 *    means and a chart of 10-second samples are different claims about the
 *    world, and the reader is entitled to know which one is on screen.
 *  - Every kWh in the energy table sits next to its coverage.
 *  - Battery temperature is a written sentence, not an empty chart.
 *  - The agent/host panel is given the same weight as the electrical data,
 *    because "why is this unit quiet" is usually answered by the wifi SSID, the
 *    disk, or the clock — not by the battery.
 */

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { api } from '../api/client';
import type { Fault, Resolution } from '../api/types';
import { useAsync } from '../components/useAsync';
import { Co2ePanel } from '../components/Co2ePanel';
import { Chart, COLOURS } from '../components/Chart';
import { Coverage, covLevel, LivenessPill, NotOnThisModel } from '../components/indicators';
import {
  DASH,
  bytesMb,
  duration,
  kwh,
  num,
  since,
  stamp,
  unitName,
  watts,
} from '../components/format';
import { useTheme } from '../App';

const WINDOWS = [
  { key: '1h', label: '1 h', ms: 3600e3, bucket: 'hour' as const },
  { key: '6h', label: '6 h', ms: 6 * 3600e3, bucket: 'hour' as const },
  { key: '24h', label: '24 h', ms: 24 * 3600e3, bucket: 'hour' as const },
  { key: '7d', label: '7 d', ms: 7 * 86400e3, bucket: 'hour' as const },
  { key: '30d', label: '30 d', ms: 30 * 86400e3, bucket: 'day' as const },
  { key: '365d', label: '1 y', ms: 365 * 86400e3, bucket: 'day' as const },
] as const;

type WindowKey = (typeof WINDOWS)[number]['key'];

const RESOLUTION_NOTE: Record<string, string> = {
  raw: 'raw 10-second samples, exactly as the unit reported them',
  '1m': 'one-minute buckets — a weighted mean over the samples in each minute',
  '1h': 'one-hour buckets — a weighted mean over the samples in each hour',
  '1d': 'one-day buckets — a weighted mean over the samples in each day',
};

export function Unit() {
  const { id = '' } = useParams();
  const [theme] = useTheme();
  const [windowKey, setWindowKey] = useState<WindowKey>('24h');
  const [resolution, setResolution] = useState<Resolution>('auto');

  const win = WINDOWS.find((w) => w.key === windowKey) ?? WINDOWS[2];

  // The window is anchored to an explicit instant rather than to `Date.now()`
  // at render time, and the anchor only moves while LIVE is on.
  //
  // That distinction is worth a control of its own. A chart that silently
  // re-fetches under you loses your zoom and, worse, means two people looking at
  // "the last hour" ten minutes apart are looking at different hours without
  // either of them knowing. Live on: it follows, once a minute. Live off: the
  // window is frozen and stays comparable while you work on it.
  const [live, setLive] = useState(true);
  const [anchor, setAnchor] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    setAnchor(Date.now());
    const id = setInterval(() => setAnchor(Date.now()), 60_000);
    return () => clearInterval(id);
  }, [live]);

  // Snapped to the minute, so a re-render inside the same minute does not shift
  // the window under the chart and make it look like the data moved.
  const range = useMemo(() => {
    const to = new Date(Math.floor(anchor / 60000) * 60000);
    return { from: new Date(to.getTime() - win.ms), to };
  }, [win.ms, anchor]);

  const detail = useAsync(() => api.unit(id), [id], { pollMs: 30_000 });
  const series = useAsync(
    () =>
      api.series(id, {
        from: range.from,
        to: range.to,
        resolution,
        metrics: ['ac_out_w', 'ac_in_w', 'pv_w', 'pack_power_w', 'soc_pct', 'inverter_temp_c'],
      }),
    [id, range.from.getTime(), range.to.getTime(), resolution],
  );
  const energy = useAsync(
    () => api.energy(id, { from: range.from, to: range.to, bucket: win.bucket }),
    [id, range.from.getTime(), range.to.getTime(), win.bucket],
  );
  const faults = useAsync(() => api.faults(id), [id]);
  /* CO2e. The preview computes on request and stores nothing; the reports
     are what count toward the public total. Both are fetched because when
     they disagree that difference is information, not a conflict. */
  const co2e = useAsync(() => api.co2e(id, { shootDayLengthH: 12 }), [id]);
  const co2eReports = useAsync(() => api.co2eReports(id), [id]);

  const unit = detail.data?.unit;
  const model = detail.data?.model;

  const powerSeries = useMemo(
    () => [
      { key: 'ac_out_w', label: 'AC output', stroke: COLOURS.out },
      { key: 'ac_in_w', label: 'Mains in', stroke: COLOURS.in },
      { key: 'pv_w', label: 'Solar', stroke: COLOURS.pv },
    ],
    [],
  );
  const socSeries = useMemo(
    () => [{ key: 'soc_pct', label: 'State of charge', stroke: COLOURS.soc, digits: 1 }],
    [],
  );
  const tempSeries = useMemo(
    () => [{ key: 'inverter_temp_c', label: 'Inverter', stroke: COLOURS.temp, digits: 1 }],
    [],
  );

  if (detail.error) {
    return (
      <div className="page">
        <div className="note bad">{detail.error}</div>
        <p>
          <Link to="/fleet">Back to fleet</Link>
        </p>
      </div>
    );
  }
  if (!unit || !model) return <div className="page hint">Loading…</div>;

  const silent = unit.liveness === 'never_reported';

  return (
    <div className="page">
      <div className="page-head">
        <h1>{unitName(unit)}</h1>
        <LivenessPill liveness={unit.liveness} />
        <span className="sub">
          {unit.display_name} · {model.name} ({model.code})
          {unit.cpu_serial ? ` · ${unit.cpu_serial}` : ' · no serial recorded'}
        </span>
        <div className="spacer" />
        <Link to="/fleet">← Fleet</Link>
      </div>

      {silent && (
        <div className="note warn" style={{ marginBottom: 14 }}>
          <strong>This unit has never reported.</strong> It is on the fleet list because it
          exists on paper — everything below is empty because there is nothing to show, not
          because the page failed to load.
          {model.unit_notes ? <div style={{ marginTop: 6 }}>{model.unit_notes}</div> : null}
        </div>
      )}

      {/* ------------------------------------------------------------ tiles */}
      <div className="tiles" style={{ marginBottom: 14 }}>
        <div className="tile">
          <div className="k">State of charge</div>
          <div className="v">
            {unit.soc_pct === null ? DASH : `${unit.soc_pct.toFixed(1)}`}
            {unit.soc_pct !== null && <small>%</small>}
          </div>
          <div className="n">
            {unit.remaining_capacity_ah === null
              ? 'BMS not reporting'
              : unit.total_capacity_ah === null
                ? `${unit.remaining_capacity_ah.toFixed(0)} Ah remaining`
                : `${unit.remaining_capacity_ah.toFixed(0)} of ${unit.total_capacity_ah.toFixed(0)} Ah`}
          </div>
        </div>
        <div className="tile">
          <div className="k">AC output</div>
          <div className="v">{watts(unit.ac_out_active_w)}</div>
          <div className="n">
            {unit.load_pct !== null ? `${unit.load_pct.toFixed(0)}% of rating` : DASH}
          </div>
        </div>
        <div className="tile">
          <div className="k">Mains in</div>
          <div className="v">{watts(unit.ac_in_power_w)}</div>
          <div className="n">{unit.working_mode_label ?? 'mode unknown'}</div>
        </div>
        <div className="tile">
          <div className="k">Solar</div>
          <div className="v">{watts(unit.pv_power_w)}</div>
          <div className="n">PV input</div>
        </div>
        <div className="tile">
          <div className="k">Pack voltage</div>
          <div className="v">
            {num(unit.pack_voltage_v, 1)}
            {unit.pack_voltage_v !== null && <small>V</small>}
          </div>
          <div className="n">nominal {num(model.nominal_pack_voltage_v, 0)} V</div>
        </div>
        <div className="tile">
          <div className="k">Inverter temp</div>
          <div className="v">
            {num(unit.inverter_temp_c, 1)}
            {unit.inverter_temp_c !== null && <small>°C</small>}
          </div>
          <div className="n">electronics, not the battery</div>
        </div>
        {/* Battery temperature: stated, not charted, not zeroed. */}
        <div className="tile unavailable">
          <div className="k">Battery temp</div>
          <div className="v">
            {model.has_battery_temp ? DASH : 'not available on this model'}
          </div>
          <div className="n">{model.has_battery_temp ? '' : 'no BMS temperature slot'}</div>
        </div>
        <div className={`tile${unit.open_faults > 0 ? ' alarm' : ''}`}>
          <div className="k">Open faults</div>
          <div className="v">{unit.open_faults}</div>
          <div className="n">{unit.open_warnings} warnings</div>
        </div>
      </div>

      {/* ----------------------------------------------------------- charts */}
      <div className="panel">
        <header>
          Telemetry
          <div className="spacer" />
          <div className="segmented">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                aria-pressed={windowKey === w.key}
                onClick={() => setWindowKey(w.key)}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="segmented">
            {(['auto', 'raw', '1m', '1h', '1d'] as const).map((r) => (
              <button key={r} aria-pressed={resolution === r} onClick={() => setResolution(r)}>
                {r}
              </button>
            ))}
          </div>
          <div className="segmented">
            <button
              aria-pressed={live}
              onClick={() => setLive(true)}
              title="The window follows the clock, once a minute."
            >
              Live
            </button>
            <button
              aria-pressed={!live}
              onClick={() => setLive(false)}
              title="Freeze the window so it stays comparable while you work on it."
            >
              Frozen
            </button>
          </div>
        </header>
        <div className="body">
          {series.error && <div className="note bad">{series.error}</div>}
          {series.data && (
            <div className="note" style={{ marginBottom: 8 }}>
              <span style={{ color: 'var(--dim)' }}>
                {stamp(series.data.from)} → {stamp(series.data.to)}
                {live ? ' (following)' : ' (frozen)'}
                {' · '}
              </span>
              Showing <strong>{RESOLUTION_NOTE[series.data.resolution]}</strong> —{' '}
              {series.data.points.toLocaleString()} points from{' '}
              {series.data.samples.toLocaleString()} samples.
              {series.data.suspect_samples > 0 && (
                <>
                  {' '}
                  <strong style={{ color: 'var(--warn)' }}>
                    {series.data.suspect_samples.toLocaleString()} of those samples
                  </strong>{' '}
                  came off a clock the server could not trust — these units have no RTC, so
                  their position on the time axis is approximate. They are shown, not hidden.
                </>
              )}
              {series.data.from_rollup && (
                <> The newest bucket may not be materialised yet; use <code>raw</code> for now.</>
              )}
            </div>
          )}
          {series.data && series.data.points === 0 && (
            <div className="unavailable-panel">
              <strong>No samples in this window</strong>
              That is a fact about the unit, not about the query.
            </div>
          )}
          {series.data && series.data.points > 0 && (
            <>
              <Chart
                t={series.data.t}
                data={series.data.series}
                series={powerSeries}
                unit="W"
                height={210}
                theme={theme}
              />
              <Chart
                t={series.data.t}
                data={series.data.series}
                series={socSeries}
                unit="%"
                height={130}
                theme={theme}
                range={[0, 100]}
              />
              {/* Inverter electronics temperature. Always available. */}
              <Chart
                t={series.data.t}
                data={series.data.series}
                series={tempSeries}
                unit="°C"
                height={130}
                theme={theme}
              />
              {/* Battery temperature is a different quantity and this product
                  does not measure it. Said in words rather than drawn as an
                  empty chart, which would read as a sensor that stopped. */}
              {!model.has_battery_temp && (
                <NotOnThisModel
                  what="Battery temperature"
                  why="The BMS register exists but the inverter's mirror has no slot for it, so this product never reports one. The inverter temperature plotted above is the electronics, and is not a substitute."
                />
              )}
            </>
          )}
        </div>
      </div>

      <div className="grid two">
        <div>
          {/* --------------------------------------------------------- energy */}
          <div className="panel">
            <header>
              Energy ({win.bucket === 'hour' ? 'hourly' : 'daily'})
              <div className="spacer" />
              {energy.data && (
                <span style={{ textTransform: 'none', letterSpacing: 0, fontWeight: 400 }}>
                  {energy.data.totals.buckets_returned} of{' '}
                  {energy.data.totals.buckets_expected} buckets have data
                </span>
              )}
            </header>
            <div className="body flush">
              {energy.error && <div className="note bad">{energy.error}</div>}
              {energy.data && (
                <>
                  {/* The two numbers people actually come here for. They were
                      prose once and nobody could find them at a glance, which
                      for the coverage figure is not a cosmetic problem: a kWh
                      total read without its coverage is a wrong number. Same
                      tile row as the top of the page, deliberately. */}
                  <div className="tiles" style={{ margin: 12 }}>
                    <div className="tile">
                      <div className="k">Delivered</div>
                      <div className="v">
                        {kwh(energy.data.totals.ac_out_kwh)}
                        <small>kWh</small>
                      </div>
                      <div className="n">AC output over this window</div>
                    </div>
                    <div className={`tile cov-tile ${covLevel(energy.data.totals.coverage)}`}>
                      <div className="k">Coverage</div>
                      <div className="v">
                        {(energy.data.totals.coverage * 100).toFixed(0)}
                        <small>%</small>
                      </div>
                      <div className="n">
                        <Coverage value={energy.data.totals.coverage} />
                      </div>
                    </div>
                    <div className="tile">
                      <div className="k">Mains in</div>
                      <div className="v">
                        {kwh(energy.data.totals.ac_in_kwh)}
                        <small>kWh</small>
                      </div>
                      <div className="n">grid energy received</div>
                    </div>
                    {/*
                      pv_kwh is null when no PV array was fitted for any part of
                      the window (migration 064). That is "this unit cannot
                      generate solar", which is a different statement from
                      0.00 kWh — and showing the zero was the bug: r223 carries a
                      per-unit standing bias with no panels attached, so the tile
                      read "0.01 kWh — PV energy harvested" on dro006, which has
                      none. Render the absence, never a number.
                    */}
                    <div className="tile">
                      <div className="k">Solar</div>
                      {energy.data.totals.pv_kwh === null ? (
                        <>
                          <div
                            className="v"
                            style={{ fontSize: 13, color: 'var(--muted)' }}
                          >
                            No PV array fitted
                          </div>
                          <div className="n">nothing to integrate</div>
                        </>
                      ) : (
                        <>
                          <div className="v">
                            {kwh(energy.data.totals.pv_kwh)}
                            <small>kWh</small>
                          </div>
                          <div className="n">PV energy harvested</div>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="hint" style={{ margin: '0 12px 12px' }}>
                    Energy is integrated over <em>sampled</em> time, so it never invents energy
                    for the hours a unit was dark. Coverage is what fraction of the window was
                    actually sampled — read it with the kWh, not after it.
                  </div>
                  <div style={{ maxHeight: 320, overflow: 'auto' }}>
                    <table className="data">
                      <thead>
                        <tr>
                          <th>{win.bucket === 'hour' ? 'Hour' : 'Day'}</th>
                          <th>Coverage</th>
                          <th className="num">Out kWh</th>
                          <th className="num">In kWh</th>
                          <th className="num">PV kWh</th>
                          <th className="num">Peak W</th>
                          <th className="num">Samples</th>
                        </tr>
                      </thead>
                      <tbody>
                        {energy.data.buckets.map((b) => (
                          <tr key={b.bucket}>
                            <td>{stamp(b.bucket)}</td>
                            <td>
                              <Coverage value={b.coverage} />
                            </td>
                            <td className="num">{kwh(b.ac_out_kwh)}</td>
                            <td className="num">{kwh(b.ac_in_kwh)}</td>
                            <td className="num">{kwh(b.pv_kwh)}</td>
                            <td className="num">{num(b.ac_out_peak_w, 0)}</td>
                            <td className="num">
                              {b.samples.toLocaleString()}
                              {b.suspect_samples > 0 && (
                                <span
                                  style={{ color: 'var(--warn)' }}
                                  title={`${b.suspect_samples} of these samples came off an untrusted clock`}
                                >
                                  {' '}
                                  ({b.suspect_samples})
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                        {energy.data.buckets.length === 0 && (
                          <tr>
                            <td colSpan={7} className="hint" style={{ padding: 14 }}>
                              No buckets in range. There is no row here rather than a row of
                              zeroes, because we measured nothing — not zero.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* --------------------------------------------------------- faults */}
          <div className="panel">
            <header>Faults and warnings</header>
            <div className="body flush">
              <FaultTable id={id} faults={faults.data ?? []} onChanged={faults.reload} />
            </div>
          </div>
        </div>

        <div>
          {/* ---------------------------------------------------- agent/host */}
          <div className="panel">
            <header>Agent and host</header>
            <div className="body">
              {detail.data?.agent ? (
                <dl className="kv">
                  <dt>Last contact</dt>
                  <dd title={detail.data.agent.last_contact_at ?? ''}>
                    {since(detail.data.agent.last_contact_at)}
                  </dd>
                  <dt>Agent</dt>
                  <dd>
                    {detail.data.agent.agent_version ?? DASH}
                    {detail.data.agent.source_schema ? ` · ${detail.data.agent.source_schema}` : ''}
                  </dd>
                  <dt>NTP</dt>
                  <dd style={{ color: detail.data.agent.ntp_synced ? undefined : 'var(--warn)' }}>
                    {detail.data.agent.ntp_synced === null
                      ? DASH
                      : detail.data.agent.ntp_synced
                        ? 'synced'
                        : 'NOT SYNCED'}
                  </dd>
                  <dt>Clock skew</dt>
                  <dd
                    style={{
                      color:
                        Math.abs(detail.data.agent.clock_skew_s ?? 0) > 5
                          ? 'var(--warn)'
                          : undefined,
                    }}
                    title="server time minus unit time at last contact. These Pis have no RTC."
                  >
                    {num(detail.data.agent.clock_skew_s, 2, ' s')}
                  </dd>
                  <dt>Wifi</dt>
                  <dd title="Every stored profile on these units autoconnects, including networks nobody chose.">
                    {detail.data.agent.wifi_ssid ?? DASH}
                    {detail.data.agent.wifi_signal !== null
                      ? ` (${detail.data.agent.wifi_signal})`
                      : ''}
                  </dd>
                  <dt>IP</dt>
                  <dd>{detail.data.agent.ip_address ?? DASH}</dd>
                  <dt>Uptime</dt>
                  <dd>{duration(detail.data.agent.uptime_s)}</dd>
                  <dt>Disk free</dt>
                  <dd
                    style={{
                      color:
                        (detail.data.agent.disk_free_mb ?? Infinity) < 500
                          ? 'var(--bad)'
                          : undefined,
                    }}
                  >
                    {bytesMb(detail.data.agent.disk_free_mb)}
                  </dd>
                  <dt>Local DB</dt>
                  <dd>{bytesMb(detail.data.agent.db_size_mb)}</dd>
                  <dt>Rows pending</dt>
                  <dd>{detail.data.agent.rows_pending?.toLocaleString() ?? DASH}</dd>
                  <dt>Backfill</dt>
                  <dd>{detail.data.agent.backfill_complete ? 'complete' : 'in progress'}</dd>
                  {detail.data.agent.last_error && (
                    <>
                      <dt>Last error</dt>
                      <dd style={{ color: 'var(--bad)' }}>{detail.data.agent.last_error}</dd>
                    </>
                  )}
                </dl>
              ) : (
                <div className="unavailable-panel">
                  <strong>No agent has ever checked in</strong>
                  Nothing has run on this unit, or nothing has reached us from it.
                </div>
              )}
            </div>
          </div>

          {/* ------------------------------------------------------ placement */}
          <div className="panel">
            <header>Placement</header>
            <div className="body">
              <div className="note" style={{ marginBottom: 10 }}>
                There is no GPS in any unit. Location is entered by hand or it is unknown.
              </div>
              {detail.data?.placement ? (
                <dl className="kv">
                  <dt>Now at</dt>
                  <dd>{detail.data.placement.location_name ?? 'unknown (recorded as moved)'}</dd>
                  <dt>Since</dt>
                  <dd title={detail.data.placement.from_ts}>
                    {stamp(detail.data.placement.from_ts)}
                  </dd>
                  <dt>Entered by</dt>
                  <dd>{detail.data.placement.entered_by ?? DASH}</dd>
                </dl>
              ) : (
                <p className="hint" style={{ margin: 0 }}>
                  Nobody has recorded where this unit is. Assign it on the{' '}
                  <Link to="/locations">Locations</Link> screen.
                </p>
              )}

              {(detail.data?.placement_history.length ?? 0) > 1 && (
                <table className="data" style={{ marginTop: 12 }}>
                  <thead>
                    <tr>
                      <th>Location</th>
                      <th>From</th>
                      <th>To</th>
                      <th>By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detail.data?.placement_history.map((p) => (
                      <tr key={p.id}>
                        <td>{p.location_name ?? <span className="nodata">unknown</span>}</td>
                        <td>{stamp(p.from_ts)}</td>
                        <td>{p.to_ts ? stamp(p.to_ts) : <span className="hint">current</span>}</td>
                        <td>{p.entered_by ?? DASH}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* ----------------------------------------------------------- CO2e */}
          <div className="panel">
            <header>CO₂e</header>
            <div className="body">
              <Co2ePanel
                preview={co2e.data}
                reports={co2eReports.data?.reports ?? null}
                error={co2e.error}
              />
            </div>
          </div>

          {/* ---------------------------------------------------------- model */}
          <div className="panel">
            <header>Unit and product</header>
            <div className="body">
              <dl className="kv">
                <dt>Product</dt>
                <dd>
                  {model.name} ({model.code})
                </dd>
                <dt>Inverter</dt>
                <dd>{model.inverter_model ?? DASH}</dd>
                <dt>Register map</dt>
                <dd>{model.register_map}</dd>
                <dt>Chemistry</dt>
                <dd>{model.chemistry ?? DASH}</dd>
                <dt>Max output</dt>
                <dd>{num(model.max_output_kw, 1, ' kW')}</dd>
                <dt>Usable capacity</dt>
                <dd>
                  {model.usable_capacity_kwh === null ? (
                    <span
                      className="nodata"
                      title="Deliberately unset. The units report their own capacity in BMS register 771; this is filled in from observation, not from a guess."
                    >
                      not yet observed
                    </span>
                  ) : (
                    `${model.usable_capacity_kwh} kWh`
                  )}
                </dd>
                <dt>Battery temp</dt>
                <dd>{model.has_battery_temp ? 'reported' : 'not available on this model'}</dd>
                <dt>Pi</dt>
                <dd>{model.pi_model ?? DASH}</dd>
                <dt>HAT</dt>
                <dd>
                  {model.hat_fitted === null ? DASH : model.hat_fitted ? 'fitted' : 'not fitted'}
                </dd>
                <dt>Status</dt>
                <dd>{unit.status}</dd>
                <dt>Sticker</dt>
                <dd title="Display only. Explicitly not an identifier.">{unit.label ?? DASH}</dd>
              </dl>
              {model.unit_notes && (
                <div className="note" style={{ marginTop: 10 }}>
                  {model.unit_notes}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function FaultTable({
  id,
  faults,
  onChanged,
}: {
  id: string;
  faults: Fault[];
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState<number | null>(null);

  async function ack(fault: Fault) {
    setBusy(fault.id);
    try {
      await api.ackFault(id, fault.id, {});
      onChanged();
    } finally {
      setBusy(null);
    }
  }

  if (faults.length === 0) {
    return (
      <div className="unavailable-panel">
        <strong>No faults recorded</strong>
        Nothing has been asserted by this unit.
      </div>
    );
  }

  return (
    <table className="data">
      <thead>
        <tr>
          <th>Kind</th>
          <th>Code</th>
          <th>Name</th>
          <th>Started</th>
          <th>Ended</th>
          <th className="num">Seen</th>
          <th>Acknowledged</th>
          <th />
        </tr>
      </thead>
      <tbody>
        {faults.map((f) => (
          <tr key={f.id}>
            <td>
              <span className={`pill ${f.is_warning ? 'neutral' : 'bad'}`}>
                {f.is_warning ? 'warning' : 'fault'}
              </span>
            </td>
            <td className="num">{f.code}</td>
            <td className="wrap">
              {f.name ?? <span className="nodata">unnamed code</span>}
              {f.clock_suspect && (
                <span
                  className="hint"
                  title="This interval's timestamps came off an untrusted clock. It happened; when is the uncertain part."
                >
                  {' '}
                  · clock suspect
                </span>
              )}
            </td>
            <td title={f.started_at}>{stamp(f.started_at)}</td>
            <td>
              {f.ended_at ? (
                stamp(f.ended_at)
              ) : (
                <span className="pill bad">open</span>
              )}
            </td>
            <td className="num" title="Observations rolled into this one interval">
              {f.observation_count.toLocaleString()}
            </td>
            <td>
              {f.acknowledged_at ? (
                <span className="hint" title={f.acknowledged_at}>
                  {f.acknowledged_by ?? 'yes'} · {since(f.acknowledged_at)}
                </span>
              ) : (
                <span className="hint">—</span>
              )}
            </td>
            <td>
              <button
                className="small"
                disabled={busy === f.id}
                onClick={() => void ack(f)}
                title="Records that a person has seen this. Does NOT close it — ended_at belongs to the inverter."
              >
                {f.acknowledged_at ? 'Re-ack' : 'Ack'}
              </button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
